import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Edit2, Share2, Check, X, Book, Sparkles, Trash2, ImagePlus, Type, Copy, ExternalLink, ChevronUp, ChevronDown, XCircle, RefreshCw, Globe, Lock, Palette, Wand2, Image as ImageIcon, Flame } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_THEME = { primary: "#7A9B7E", secondary: "#9FB89F", gradient_from: "#9FB89F", gradient_to: "#5A7B5E", accent: "#E5D4C1", text: "#2D3A2D" };

const TEMPLATE_THEMES = {
  freestyle: DEFAULT_THEME,
  family_history: { primary: "#8B6914", secondary: "#C4A35A", gradient_from: "#A67C2E", gradient_to: "#6B4F12", accent: "#FDF6E3", text: "#3D2B00" },
  travel_journal: { primary: "#2563EB", secondary: "#60A5FA", gradient_from: "#1E40AF", gradient_to: "#3B82F6", accent: "#DBEAFE", text: "#1E293B" },
  career: { primary: "#1E3A5F", secondary: "#4A7AB5", gradient_from: "#1E3A5F", gradient_to: "#2D5F8A", accent: "#E8F0FE", text: "#0F1B2D" },
  childhood: { primary: "#D97706", secondary: "#FBBF24", gradient_from: "#B45309", gradient_to: "#D97706", accent: "#FEF3C7", text: "#451A03" },
  love_story: { primary: "#BE185D", secondary: "#EC4899", gradient_from: "#9D174D", gradient_to: "#BE185D", accent: "#FCE7F3", text: "#500724" },
  personal_growth: { primary: "#059669", secondary: "#34D399", gradient_from: "#047857", gradient_to: "#059669", accent: "#D1FAE5", text: "#022C22" },
  adventure: { primary: "#B45309", secondary: "#F59E0B", gradient_from: "#92400E", gradient_to: "#B45309", accent: "#FEF3C7", text: "#451A03" },
  immigration: { primary: "#4338CA", secondary: "#818CF8", gradient_from: "#3730A3", gradient_to: "#4338CA", accent: "#E0E7FF", text: "#1E1B4B" },
  war_service: { primary: "#475569", secondary: "#94A3B8", gradient_from: "#334155", gradient_to: "#475569", accent: "#F1F5F9", text: "#0F172A" },
};

const SocialShareButtons = ({ url, title, summary }) => {
  const encodedUrl = encodeURIComponent(url);
  const shareText = summary || "Read my life story on My Story";
  const encodedShareText = encodeURIComponent(shareText);

  const platforms = [
    {
      name: "WhatsApp",
      href: `https://wa.me/?text=${encodedShareText}%0A%0A${encodedUrl}`,
      color: "bg-[#25D366]",
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      )
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedShareText}`,
      color: "bg-[#1877F2]",
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      )
    },
    {
      name: "X",
      href: `https://twitter.com/intent/tweet?text=${encodedShareText}&url=${encodedUrl}`,
      color: "bg-[#0f1419]",
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      )
    },
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "bg-[#0A66C2]",
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
      )
    }
  ];

  return (
    <div className="flex gap-2" data-testid="social-share-buttons">
      {platforms.map(p => (
        <a
          key={p.name}
          href={p.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${p.color} text-white p-2.5 rounded-xl hover:opacity-90 transition-opacity`}
          title={`Share on ${p.name}`}
          data-testid={`share-${p.name.toLowerCase()}`}
        >
          {p.icon}
        </a>
      ))}
    </div>
  );
};

const StoryCanvas = ({ storyData, onRefresh, onUploadImage, activeBook, onEndStory }) => {
  const [editingChapter, setEditingChapter] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [editedTitle, setEditedTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [ogShareUrl, setOgShareUrl] = useState("");
  const [socialSummary, setSocialSummary] = useState("");
  const [deletingChapter, setDeletingChapter] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingChapterId, setUploadingChapterId] = useState(null);
  const [uploadMode, setUploadMode] = useState(null);

  const { state, chapters, categorized_chapters } = storyData;
  const categories = categorized_chapters ? Object.keys(categorized_chapters) : [];

  const bookTitle = activeBook?.title || "My Story";
  const isPublic = activeBook?.is_public || false;
  const templateId = activeBook?.template || "freestyle";
  const theme = TEMPLATE_THEMES[templateId] || DEFAULT_THEME;
  const coverImage = activeBook?.cover_image || "";

  const handleTemplateChange = async (newTemplateId) => {
    if (!activeBook) return;
    try {
      await axios.put(`${API}/books/${activeBook.id}/template`, { template: newTemplateId });
      await onRefresh();
      toast.success("Template updated!");
    } catch {
      toast.error("Failed to update template");
    }
  };

  const handleSaveEdit = async (chapterId) => {
    try {
      await axios.put(`${API}/story/edit`, { chapter_id: chapterId, new_content: editedContent });
      await onRefresh();
      setEditingChapter(null);
      toast.success("Chapter updated!");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleSaveTitle = async (chapterId) => {
    try {
      await axios.put(`${API}/story/chapter/${chapterId}/title`, { title: editedTitle });
      await onRefresh();
      setEditingTitle(null);
      toast.success("Title updated!");
    } catch (error) {
      toast.error("Failed to update title");
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    try {
      await axios.delete(`${API}/story/chapter/${chapterId}`);
      await onRefresh();
      setDeletingChapter(null);
      toast.success("Chapter removed");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeletePhoto = async (chapterId) => {
    try {
      await axios.delete(`${API}/story/chapter/${chapterId}/photo`);
      await onRefresh();
      toast.success("Photo removed");
    } catch (error) {
      toast.error("Failed to remove photo");
    }
  };

  const handleShare = async () => {
    try {
      const response = await axios.get(`${API}/story/share`);
      const token = response.data.share_token;
      const url = `${window.location.origin}/share/${token}`;
      const ogUrl = `${BACKEND_URL}/api/og/share/${token}`;
      setShareUrl(url);
      setOgShareUrl(ogUrl);
      setSocialSummary(response.data.social_summary || "");
      setShowShareModal(true);
    } catch (error) {
      toast.error("Failed to generate link");
    }
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleTogglePublic = async () => {
    if (!activeBook) return;
    try {
      await axios.put(`${API}/books/${activeBook.id}/public`, { is_public: !isPublic });
      await onRefresh();
      toast.success(!isPublic ? "Story is now public and visible on the homepage!" : "Story is now private");
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const [aiBusy, setAiBusy] = useState(null); // 'title' | 'cover' | null

  const handleAITitle = async () => {
    if (!activeBook || aiBusy) return;
    setAiBusy("title");
    const tid = toast.loading("Asking AI for a fresh title…");
    try {
      const res = await axios.post(`${API}/books/${activeBook.id}/suggest-title`);
      await onRefresh();
      toast.success(`Renamed to "${res.data.title}"!`, { id: tid, duration: 5000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't suggest a title", { id: tid });
    } finally {
      setAiBusy(null);
    }
  };

  const handleAICover = async () => {
    if (!activeBook || aiBusy) return;
    setAiBusy("cover");
    const tid = toast.loading("Painting your cover with AI… ~20s");
    try {
      await axios.post(`${API}/books/${activeBook.id}/suggest-cover`);
      await onRefresh();
      toast.success("Your beautiful new cover is ready!", { id: tid, duration: 5000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate a cover", { id: tid });
    } finally {
      setAiBusy(null);
    }
  };

  const handleFileSelect = (chapterId, mode) => {
    setUploadingChapterId(chapterId);
    setUploadMode(mode);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && uploadingChapterId && onUploadImage) {
      await onUploadImage(file, uploadingChapterId);
      setUploadingChapterId(null);
      setUploadMode(null);
    }
    e.target.value = "";
  };

  const startEditContent = (chapter) => {
    setEditingChapter(chapter.id);
    setEditedContent(chapter.content);
    setEditingTitle(null);
  };

  const startEditTitle = (chapter) => {
    setEditingTitle(chapter.id);
    setEditedTitle(chapter.title);
    setEditingChapter(null);
  };

  const moveChapter = useCallback(async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= chapters.length) return;
    const reordered = Array.from(chapters);
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);
    const newOrder = reordered.map((ch) => ch.id);
    try {
      await axios.put(`${API}/story/reorder`, { chapter_ids: newOrder });
      await onRefresh();
    } catch {
      toast.error("Failed to reorder");
    }
  }, [chapters, onRefresh]);

  // Empty state — show only if no chapters exist
  if (!chapters || chapters.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-[#F8F6F1] p-12 relative overflow-hidden">
        <div className="absolute top-10 right-20 w-96 h-96 bg-[#9FB89F]/10 organic-blob" />
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#E5D4C1]/20 organic-blob" style={{ animationDelay: "2s" }} />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-2xl relative z-10">
          <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center shadow-xl organic-blob">
            <Book className="w-16 h-16 text-white" />
          </div>
          <h2 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-[#4A5D4A] mb-6 leading-tight">Your Story Canvas</h2>
          <p className="font-sans text-base lg:text-lg text-[#7A8A7A] leading-relaxed mb-6">Start chatting to build your memoir. Your story will appear here as a beautifully crafted book.</p>
          <div className="flex items-center justify-center gap-2 text-[#7A9B7E]">
            <Sparkles className="w-5 h-5" />
            <span className="font-sans text-sm">AI-powered memoir creation</span>
          </div>
        </motion.div>
      </div>
    );
  }

  const displayChapters = selectedCategory === "all" ? chapters : categorized_chapters[selectedCategory] || [];
  const canReorder = selectedCategory === "all";
  const storySummary = chapters[0]?.content?.slice(0, 150) + "..." || "";

  return (
    <div className="h-full overflow-y-auto bg-[#F8F6F1] relative">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#9FB89F]/5 organic-blob" />
      <div className="absolute bottom-40 left-20 w-[400px] h-[400px] bg-[#E5D4C1]/10 organic-blob" style={{ animationDelay: "3s" }} />

      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />

      {/* Header bar */}
      <div className="sticky top-0 z-20 bg-white/60 backdrop-blur-md border-b border-[#D4C9B8]/30 px-4 py-2">
        <div className="max-w-6xl mx-auto space-y-1.5">
          {/* Row 1: Template + Public + Share */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button onClick={() => setShowTemplatePicker(true)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-sans bg-white/80 text-[#7A8A7A] border border-[#D4C9B8]/50 hover:bg-[#F8F6F1] transition-all" data-testid="change-template-btn">
                <Palette className="w-3 h-3" /> Template
              </button>
              <button onClick={handleTogglePublic} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-sans transition-all border ${isPublic ? "bg-[#9FB89F]/20 text-[#4A5D4A] border-[#9FB89F]/40" : "bg-white/80 text-[#7A8A7A] border-[#D4C9B8]/50"}`} data-testid="toggle-public-btn">
                {isPublic ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {isPublic ? "Public" : "Private"}
              </button>
            </div>
            <div className="flex items-center gap-2">
              {chapters.length >= 3 && onEndStory && (
                <button
                  onClick={onEndStory}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-sans transition-all shadow-sm ${
                    activeBook?.baked
                      ? "bg-amber-100 text-amber-700 border border-amber-300/60 hover:bg-amber-200"
                      : "bg-gradient-to-r from-amber-500 to-rose-500 text-white hover:brightness-110"
                  }`}
                  title={activeBook?.baked ? "Already baked — re-bake to re-polish the story" : "Final AI pass: reorganize, polish, recover missing details"}
                  data-testid="end-story-bake-btn"
                >
                  <Flame className="w-3 h-3" />
                  {activeBook?.baked ? "Re-bake" : "End The Story"}
                </button>
              )}
              <button onClick={handleShare} className="bg-[#C67B5C] text-white px-3 py-1.5 rounded-full font-sans font-medium text-[11px] hover:bg-[#B66B4C] transition-all flex items-center gap-1 shadow-sm" data-testid="story-share-button">
                <Share2 className="w-3 h-3" /> Share
              </button>
            </div>
          </div>
          {/* Row 2: Category filters */}
          {categories.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
              <button onClick={() => setSelectedCategory("all")} className={`px-3 py-1 rounded-full text-[11px] font-sans transition-all whitespace-nowrap ${selectedCategory === "all" ? "bg-[#7A9B7E] text-white shadow-sm" : "bg-white/80 text-[#7A8A7A] hover:bg-[#E5D4C1]/50"}`} data-testid="filter-all">
                All ({chapters.length})
              </button>
              {categories.map((cat) => (
                <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-3 py-1 rounded-full text-[11px] font-sans transition-all whitespace-nowrap ${selectedCategory === cat ? "bg-[#9FB89F] text-white shadow-sm" : "bg-white/80 text-[#7A8A7A] hover:bg-[#E5D4C1]/50"}`} data-testid={`filter-${cat}`}>
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Share modal with social buttons */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowShareModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-[#D4C9B8]/40" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
              <h3 className="font-serif text-2xl text-[#4A5D4A] mb-2">Share Your Memoir</h3>
              <p className="font-sans text-sm text-[#7A8A7A] mb-6">Anyone with this link can read your story</p>
              
              {/* Copy link */}
              <div className="flex items-center gap-2 bg-[#F8F6F1] rounded-2xl p-3 border border-[#D4C9B8]/40 mb-5">
                <input type="text" value={shareUrl} readOnly className="flex-1 bg-transparent text-sm font-sans text-[#4A5D4A] outline-none truncate" data-testid="share-url-input" />
                <button onClick={copyShareUrl} className="bg-[#7A9B7E] text-white p-2 rounded-xl hover:bg-[#6A8B6E] transition-all flex-shrink-0" data-testid="copy-share-url">
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              {/* Social media buttons */}
              <div className="mb-5">
                <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-3">Share on social media</p>
                <SocialShareButtons url={ogShareUrl} title={bookTitle} summary={socialSummary || storySummary} />
              </div>

              {/* Public toggle in modal */}
              <div className="flex items-center justify-between bg-[#F8F6F1] rounded-2xl p-4 border border-[#D4C9B8]/40 mb-5">
                <div>
                  <p className="font-sans text-sm font-medium text-[#2D3A2D]">Show on homepage</p>
                  <p className="font-sans text-xs text-[#7A8A7A]">Let others discover your story</p>
                </div>
                <button
                  onClick={handleTogglePublic}
                  className={`relative w-12 h-6 rounded-full transition-colors ${isPublic ? "bg-[#7A9B7E]" : "bg-[#D4C9B8]"}`}
                  data-testid="share-modal-public-toggle"
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isPublic ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="flex gap-3">
                <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="flex-1 text-center bg-[#F8F6F1] text-[#4A5D4A] py-3 rounded-full font-sans text-sm hover:bg-[#E5D4C1]/50 transition-all flex items-center justify-center gap-2 border border-[#D4C9B8]/40" data-testid="preview-share-link">
                  <ExternalLink className="w-4 h-4" />
                  Preview
                </a>
                <button onClick={() => setShowShareModal(false)} className="flex-1 bg-[#7A9B7E] text-white py-3 rounded-full font-sans text-sm hover:bg-[#6A8B6E] transition-all">
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template Picker */}
      <TemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelect={handleTemplateChange}
        currentTemplate={templateId}
      />

      {/* Book content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        <div className="bg-white/80 backdrop-blur-sm shadow-2xl border-2 border-[#D4C9B8]/40 rounded-[2rem] overflow-hidden">
          {/* Cover */}
          <div className="relative text-center overflow-hidden" style={{ minHeight: coverImage ? '320px' : undefined }}>
            {coverImage ? (
              <>
                <img src={coverImage} alt={bookTitle} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${theme.gradient_from}CC, ${theme.gradient_to}CC)` }} />
              </>
            ) : (
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${theme.gradient_from}, ${theme.gradient_to})` }}>
                <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 organic-blob" />
                <div className="absolute bottom-10 right-10 w-48 h-48 bg-white/10 organic-blob" style={{ animationDelay: "2s" }} />
              </div>
            )}

            {/* AI action toolbar — always visible top-right of cover */}
            {chapters.length >= 1 && (
              <div className="absolute top-3 right-3 z-20 flex flex-wrap gap-2 justify-end" data-testid="ai-cover-actions">
                <button
                  onClick={handleAITitle}
                  disabled={aiBusy !== null}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white text-[#2D3A2D] text-xs font-sans font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-wait"
                  title="AI: Suggest a new title for this memoir"
                  data-testid="ai-title-btn"
                >
                  <Wand2 className={`w-3.5 h-3.5 text-[#7A9B7E] ${aiBusy === "title" ? "animate-pulse" : ""}`} />
                  <span>{aiBusy === "title" ? "Working…" : "AI Title"}</span>
                </button>
                <button
                  onClick={handleAICover}
                  disabled={aiBusy !== null}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white text-[#2D3A2D] text-xs font-sans font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-wait"
                  title="AI: Generate a beautiful book cover"
                  data-testid="ai-cover-btn"
                >
                  <ImageIcon className={`w-3.5 h-3.5 text-[#C67B5C] ${aiBusy === "cover" ? "animate-pulse" : ""}`} />
                  <span>{aiBusy === "cover" ? "Painting…" : (coverImage ? "Re-roll Cover" : "AI Cover")}</span>
                </button>
              </div>
            )}

            <div className="relative z-10 p-12 lg:p-16">
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-serif font-bold text-5xl lg:text-7xl text-white mb-4 tracking-tight drop-shadow-lg">
                {bookTitle}
              </motion.h1>
              <p className="font-sans text-lg text-white/90">A Personal Memoir</p>
              <div className="mt-6 flex items-center justify-center gap-4 text-white/80">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-sans text-sm">{chapters.length} Chapter{chapters.length !== 1 ? "s" : ""}</span>
                </div>
                {activeBook?.genre && (
                  <span className="font-sans text-sm bg-white/20 px-3 py-1 rounded-full">{activeBook.genre}</span>
                )}
              </div>
            </div>
          </div>


          {/* Chapters */}
          <div className="p-8 lg:p-16">
            <div className="space-y-16">
              {displayChapters.map((chapter, index) => (
                <div key={chapter.id} className="relative group" data-testid={`chapter-${index}`}>

                  {/* Reorder bar + action buttons */}
                  <div className="flex items-center justify-between mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {canReorder && displayChapters.length > 1 && (
                      <div className="flex items-center gap-1 bg-white rounded-xl shadow-sm border border-[#D4C9B8]/40 px-1" data-testid={`reorder-bar-${index}`}>
                        <button onClick={() => moveChapter(index, -1)} disabled={index === 0} className="p-1.5 hover:bg-[#F8F6F1] rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Move up" data-testid={`move-up-${index}`}>
                          <ChevronUp className="w-4 h-4 text-[#7A8A7A]" />
                        </button>
                        <span className="font-sans text-xs text-[#7A8A7A] px-1 tabular-nums">{index + 1}/{displayChapters.length}</span>
                        <button onClick={() => moveChapter(index, 1)} disabled={index === displayChapters.length - 1} className="p-1.5 hover:bg-[#F8F6F1] rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Move down" data-testid={`move-down-${index}`}>
                          <ChevronDown className="w-4 h-4 text-[#7A8A7A]" />
                        </button>
                      </div>
                    )}
                    {!canReorder && <div />}
                    <div className="flex gap-1.5" data-testid={`chapter-actions-${index}`}>
                      <button onClick={() => startEditTitle(chapter)} className="p-2 bg-white rounded-xl shadow-sm border border-[#D4C9B8]/40 hover:bg-[#F8F6F1] transition-all" title="Edit title" data-testid={`edit-title-btn-${index}`}><Type className="w-3.5 h-3.5 text-[#7A9B7E]" /></button>
                      <button onClick={() => startEditContent(chapter)} className="p-2 bg-white rounded-xl shadow-sm border border-[#D4C9B8]/40 hover:bg-[#F8F6F1] transition-all" title="Edit content" data-testid={`edit-content-btn-${index}`}><Edit2 className="w-3.5 h-3.5 text-[#7A9B7E]" /></button>
                      {chapter.image_url ? (<><button onClick={() => handleFileSelect(chapter.id, "replace")} className="p-2 bg-white rounded-xl shadow-sm border border-[#D4C9B8]/40 hover:bg-[#F8F6F1] transition-all" title="Replace photo" data-testid={`replace-photo-btn-${index}`}><RefreshCw className="w-3.5 h-3.5 text-[#C67B5C]" /></button><button onClick={() => handleDeletePhoto(chapter.id)} className="p-2 bg-white rounded-xl shadow-sm border border-red-200 hover:bg-red-50 transition-all" title="Remove photo" data-testid={`delete-photo-btn-${index}`}><XCircle className="w-3.5 h-3.5 text-red-400" /></button></>) : (<button onClick={() => handleFileSelect(chapter.id, "add")} className="p-2 bg-white rounded-xl shadow-sm border border-[#D4C9B8]/40 hover:bg-[#F8F6F1] transition-all" title="Add photo" data-testid={`add-photo-btn-${index}`}><ImagePlus className="w-3.5 h-3.5 text-[#C67B5C]" /></button>)}
                      <button onClick={() => setDeletingChapter(chapter.id)} className="p-2 bg-white rounded-xl shadow-sm border border-red-200 hover:bg-red-50 transition-all" title="Delete chapter" data-testid={`delete-btn-${index}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                    </div>
                  </div>

                  <AnimatePresence>
                    {deletingChapter === chapter.id && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mb-4 bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center justify-between" data-testid={`delete-confirm-${index}`}>
                        <p className="font-sans text-sm text-red-600">Delete this chapter?</p>
                        <div className="flex gap-2">
                          <button onClick={() => handleDeleteChapter(chapter.id)} className="bg-red-500 text-white px-4 py-1.5 rounded-full text-xs font-sans hover:bg-red-600" data-testid={`confirm-delete-${index}`}>Delete</button>
                          <button onClick={() => setDeletingChapter(null)} className="bg-white text-[#7A8A7A] px-4 py-1.5 rounded-full text-xs font-sans border border-[#D4C9B8]">Cancel</button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mb-5">
                    {chapter.category && (<span className="inline-block px-4 py-1.5 bg-[#9FB89F]/20 text-[#5A7B5E] text-xs font-sans font-medium rounded-full mb-3 border border-[#9FB89F]/30">{chapter.category}</span>)}
                    {editingTitle === chapter.id ? (
                      <div className="flex items-center gap-3">
                        <input type="text" value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} className="flex-1 font-serif font-bold text-3xl text-[#4A5D4A] bg-[#F8F6F1] border-2 border-[#9FB89F] rounded-2xl px-4 py-2 outline-none" autoFocus data-testid={`title-input-${index}`} />
                        <button onClick={() => handleSaveTitle(chapter.id)} className="p-2 bg-[#7A9B7E] text-white rounded-xl shadow-md" data-testid={`save-title-${index}`}><Check className="w-5 h-5" /></button>
                        <button onClick={() => setEditingTitle(null)} className="p-2 bg-[#E5D4C1] text-[#6A5A4A] rounded-xl"><X className="w-5 h-5" /></button>
                      </div>
                    ) : (<h2 className="font-serif font-bold text-3xl lg:text-4xl text-[#4A5D4A] mb-1">{chapter.title}</h2>)}
                    {chapter.time_period && <p className="font-sans text-sm text-[#7A8A7A] italic mt-1">{chapter.time_period}</p>}
                  </div>

                  {chapter.image_url && (
                    <div className="mb-8 relative group/img">
                      <img src={chapter.image_url} alt={chapter.title} loading="lazy" className="w-full max-w-3xl h-80 object-cover rounded-3xl shadow-lg border-4 border-white" />
                      <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-all rounded-3xl flex items-center justify-center opacity-0 group-hover/img:opacity-100">
                        <div className="flex gap-2">
                          <button onClick={() => handleFileSelect(chapter.id, "replace")} className="bg-white/90 backdrop-blur-sm text-[#4A5D4A] px-4 py-2 rounded-full text-sm font-sans flex items-center gap-2 shadow-lg hover:bg-white" data-testid={`img-replace-btn-${index}`}><RefreshCw className="w-4 h-4" />Replace</button>
                          <button onClick={() => handleDeletePhoto(chapter.id)} className="bg-white/90 backdrop-blur-sm text-red-500 px-4 py-2 rounded-full text-sm font-sans flex items-center gap-2 shadow-lg hover:bg-white" data-testid={`img-delete-btn-${index}`}><Trash2 className="w-4 h-4" />Remove</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {editingChapter === chapter.id ? (
                    <div className="space-y-4">
                      <textarea value={editedContent} onChange={(e) => setEditedContent(e.target.value)} className="w-full font-sans text-base text-[#4A5D4A] leading-relaxed bg-[#F8F6F1] border-2 border-[#9FB89F] rounded-2xl p-6 min-h-[250px] outline-none resize-y" autoFocus data-testid={`content-textarea-${index}`} />
                      <div className="flex gap-3">
                        <button onClick={() => handleSaveEdit(chapter.id)} className="bg-[#7A9B7E] text-white px-6 py-2.5 rounded-full font-sans text-sm flex items-center gap-2 shadow-md hover:bg-[#6A8B6E]" data-testid={`save-content-${index}`}><Check className="w-4 h-4" />Save</button>
                        <button onClick={() => setEditingChapter(null)} className="bg-[#E5D4C1] text-[#6A5A4A] px-6 py-2.5 rounded-full font-sans text-sm flex items-center gap-2 hover:bg-[#D4C9B8]"><X className="w-4 h-4" />Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="font-sans text-lg text-[#4A5D4A] leading-relaxed">
                        <div className="first-letter:text-6xl first-letter:font-serif first-letter:font-bold first-letter:text-[#7A9B7E] first-letter:float-left first-letter:mr-3 first-letter:leading-none">{chapter.content}</div>
                      </div>
                      {chapter.themes && chapter.themes.length > 0 && (
                        <div className="mt-6 flex flex-wrap gap-2">
                          {chapter.themes.map((th, i) => (<span key={i} className="px-3 py-1 bg-[#E5D4C1]/40 text-[#7A6A5A] text-xs font-sans rounded-full">{th}</span>))}
                        </div>
                      )}
                    </div>
                  )}

                  {index < displayChapters.length - 1 && (
                    <div className="mt-16 flex justify-center"><div className="w-32 h-px bg-gradient-to-r from-transparent via-[#D4C9B8] to-transparent" /></div>
                  )}
                </div>
              ))}
            </div>


            {state?.phase === "complete" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-24 text-center border-t border-[#D4C9B8]/50 pt-12">
                <p className="font-serif text-2xl italic text-[#7A8A7A] mb-3">The End</p>
                <p className="font-sans text-sm text-[#A0AEC0]">Created with My Story</p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryCanvas;
